import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useReducedMotion } from '../../hooks/useReducedMotion';

/**
 * ArchitectureStack — GoingMerry's three layers, top to bottom, with data packets flowing down the connectors
 * Packets run only while the diagram is on screen; reduced motion shows the static diagram.
 *
 * Importers: src/pages/GoingMerryPage.jsx ("Under the Hood")
 * Data schema: No props. Layers are fixed (see LAYERS).
 */
const LAYERS = [
  { number: '01', title: 'Go Orchestrator', detail: 'Async Scheduler & Memory Broker', detailClass: 'text-[#62B7FF]/80' },
  { number: '02', title: 'C/C++ Tensor Engine', detail: 'Hardware Compiler & L3 Cache Control', detailClass: 'text-[#62B7FF]/80' },
  { number: '03', title: 'CUDA / Apple Metal / AVX', detail: 'Local operations', detailClass: 'text-emerald-400' },
];

const CONNECTOR_HEIGHT = 44;
const PACKETS = [0, 0.55, 1.1]; // start offsets in seconds, so the packets trail one another

const Connector = ({ flowing }) => (
  <div data-testid="stack-connector" aria-hidden="true" className="relative w-px mx-auto bg-gradient-to-b from-[#62B7FF]/15 via-[#62B7FF]/40 to-[#62B7FF]/15" style={{ height: CONNECTOR_HEIGHT }}>
    {flowing &&
      PACKETS.map((offset) => (
        <motion.span
          key={offset}
          data-testid="stack-packet"
          className="absolute left-1/2 -ml-[3px] w-1.5 h-1.5 rounded-full bg-[#62B7FF] shadow-[0_0_8px_#62B7FF]"
          initial={{ y: -3, opacity: 0 }}
          animate={{ y: CONNECTOR_HEIGHT - 3, opacity: [0, 1, 1, 0] }}
          transition={{ duration: 1.6, delay: offset, repeat: Infinity, ease: 'easeInOut' }}
        />
      ))}
  </div>
);

const ArchitectureStack = () => {
  const reducedMotion = useReducedMotion();
  const rootRef = useRef(null);
  const [onScreen, setOnScreen] = useState(false);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    const observer = new IntersectionObserver((entries) => entries.forEach((e) => setOnScreen(e.isIntersecting)), { threshold: 0.25 });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const state = reducedMotion ? 'static' : onScreen ? 'flowing' : 'idle';

  return (
    <div ref={rootRef} data-testid="architecture-stack" data-state={state} className="w-full max-w-md mx-auto py-6">
      <ol aria-label="GoingMerry architecture, top to bottom" className="flex flex-col">
        {LAYERS.map((layer, i) => (
          <React.Fragment key={layer.number}>
            <li className="px-6 py-4 bg-[#101A2B] border border-white/10 rounded-2xl text-center shadow-lg">
              <span className="block mb-1 text-[10px] font-mono tracking-widest text-[#AAB4C4]/60 uppercase">Layer {layer.number}</span>
              <h4 className="text-sm font-bold text-[#D8DDE6] tracking-tight">{layer.title}</h4>
              <span className={`block mt-1 text-[10px] font-mono ${layer.detailClass}`}>{layer.detail}</span>
            </li>
            {i < LAYERS.length - 1 && <Connector flowing={state === 'flowing'} />}
          </React.Fragment>
        ))}
      </ol>
    </div>
  );
};

export default ArchitectureStack;
